import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { Globe, ChevronDown, Users } from "lucide-react";

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest(".lang-switcher")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const links = [
    { href: "#faq", label: t.nav.faq },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass shadow-[0_8px_40px_-16px_rgba(0,0,0,0.8)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:h-28">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img
            src="/logo.png"
            alt="PreNet"
            className="h-14 w-auto lg:h-20"
            data-testid="nav-logo-image"
          />
          <span className="hidden rounded-full border border-signal/30 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-signal sm:inline-block">
            Pre-launch
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-ink-muted transition-colors hover:text-signal"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/membre"
            data-testid="nav-member-link"
            className="flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-2 text-ink-muted transition-colors hover:border-signal/40 hover:text-signal"
          >
            <Users size={15} strokeWidth={1.8} />
            <span className="hidden text-xs font-medium sm:inline">{t.nav.member}</span>
          </a>
          <div className="lang-switcher relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="language-switcher-select"
              aria-label="Language"
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-2 text-sm text-ink-muted transition-colors hover:border-signal/40 hover:text-signal"
            >
              <Globe size={15} strokeWidth={1.8} />
              <span className="font-mono text-xs uppercase">{lang}</span>
              <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="glass absolute right-0 top-12 w-44 overflow-hidden rounded-xl py-1 shadow-2xl">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`language-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      navigate(`/${l.code}${search}`);
                      setOpen(false);
                    }}
                    className={`block w-full px-4 py-2.5 text-left text-sm transition-colors ${
                      l.code === lang
                        ? "bg-signal/10 text-signal"
                        : "text-ink-muted hover:bg-white/5 hover:text-ink"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="#register"
            data-testid="nav-register-button"
            className="hidden rounded-full bg-tactical px-5 py-2.5 text-sm font-bold text-night transition-all hover:scale-[1.03] hover:glow-tactical sm:inline-block"
          >
            {t.nav.register}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Nav;
